import { useState, useEffect, useMemo } from "react";
import { FaPlus, FaDownload, FaChalkboardTeacher } from "react-icons/fa";
import DataTable from "../../components/componentsAdmin/DataTable";
import SearchAndFilters from "../../components/componentsAdmin/SearchAndFilters";
import Toast from "../../components/componentsAdmin/Toast";
import ProfessorModal from "../../components/componentsAdmin/ProfessorModal";
import { exportarProfessoresCSV } from "../../utils/exportarCsv";
import type { Professor } from "../../types/professor";
import {
  buscarTodosProfessores,
  obterEstatisticasProfessores,
  type EstatisticasProfessores,
} from "../../services/professorService";

export default function GestaoProfessores() {
  const [listProf, setListProf] = useState<Professor[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedProfessor, setSelectedProfessor] = useState<Professor | null>(
    null
  );

  // Modal para mensagens Toast
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [showToast, setShowToast] = useState(false);
  // Estados para filtros
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [especialidadeFilter, setEspecialidadeFilter] = useState("");
  const [estatisticas, setEstatisticas] =
    useState<EstatisticasProfessores | null>(null);
  // Estado para exportar CSV
  const [, setCsvLoading] = useState(false);

  const ESPECIALIDADE_COLORS: Record<string, string> = {
    Futevôlei: "bg-blue-100 text-blue-800 ",
    "Beach Tennis": "bg-pink-100 text-pink-800",
    Vôlei: "bg-purple-100 text-purple-800",
  };
  // Configuração das colunas
  const columns = [
    { key: "nome", label: "Nome" },
    { key: "email", label: "Email" },
    { key: "telefone", label: "Telefone" },
    {
      key: "especialidade",
      label: "Especialidade",
      render: (value: string) => {
        const colorClass =
          ESPECIALIDADE_COLORS[value] || "bg-gray-100 text-gray-700 ";
        return (
          <span
            className={`px-2 py-1 text-xs rounded-full font-medium ${colorClass}`}
          >
            {value}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (value: string) => {
        const colorClass =
          value === "Ativo"
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700";
        return (
          <span
            className={`px-2 py-1 text-xs rounded-full font-medium ${colorClass}`}
          >
            {value}
          </span>
        );
      },
    },
  ];

  // Configuração dos filtros
  const searchFilters = [
    {
      label: "Status",
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: "Ativo", label: "Ativo" },
        { value: "Inativo", label: "Inativo" },
      ],
    },
    {
      label: "Especialidade",
      value: especialidadeFilter,
      onChange: setEspecialidadeFilter,
      options: [
        { value: "Futevôlei", label: "Futevôlei" },
        { value: "Beach Tennis", label: "Beach Tennis" },
        { value: "Vôlei", label: "Vôlei" },
      ],
    },
  ];

  const professoresFiltrados = useMemo(() => {
    return listProf.filter((professor) => {
      // Filtro por texto de busca (nome ou email)
      const matchSearch =
        searchText === "" ||
        professor.nome.toLowerCase().includes(searchText.toLowerCase()) ||
        professor.email.toLowerCase().includes(searchText.toLowerCase());

      // Filtro por status
      const matchStatus =
        statusFilter === "" || professor.status === statusFilter;

      // Filtro por especialidade
      const matchEspecialidade =
        especialidadeFilter === "" ||
        professor.especialidade === especialidadeFilter;

      return matchSearch && matchStatus && matchEspecialidade;
    });
  }, [listProf, searchText, statusFilter, especialidadeFilter]);

  // Buscar Professores
  const fetchProfessores = async () => {
    try {
      setLoading(true);
      const professoresData = await buscarTodosProfessores();
      setListProf(professoresData);
      const estatisticasData = await obterEstatisticasProfessores();
      setEstatisticas(estatisticasData);
    } catch (error) {
      console.error("Erro ao buscar professores:", error);
      setToastMessage("Erro ao carregar professores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfessores();
  }, []);

  const handleEditProf = (professor: Professor) => {
    setIsModalOpen(true);
    setSelectedProfessor(professor);
    setModalMode("edit");
  };

  const handleExportarCSV = async () => {
    try {
      setCsvLoading(true);
      setToastMessage("Iniciando exportação CSV...");
      setToastType("success");
      setShowToast(true);

      await exportarProfessoresCSV();

      setToastMessage("CSV exportado com sucesso!");
      setToastType("success");
      setShowToast(true);
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      setToastMessage("Erro ao exportar CSV. Tente novamente.");
      setToastType("error");
      setShowToast(true);
    } finally {
      setCsvLoading(false);
    }
  };
  return (
    <div className="p-6">
      {/* 1. HEADER com título e botão "Novo Professor" */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <FaChalkboardTeacher className="text-2xl text-green-600" />
          <h1 className="text-2xl font-bold text-gray-800">
            Gestão de Professores
          </h1>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExportarCSV}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={professoresFiltrados.length === 0}
          >
            <FaDownload />
            Exportar CSV
          </button>
          <button
            onClick={() => {
              setModalMode("create");
              setIsModalOpen(true);
              setSelectedProfessor(null);
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <FaPlus />
            Novo Professor
          </button>
        </div>
      </div>

      {/* 2. ÁREA DE ESTATÍSTICAS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaChalkboardTeacher className="text-2xl text-blue-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-gray-500">
                Total de Professores
              </h3>
              {/* Total de Professores */}
              <p className="text-2xl font-bold text-gray-900">
                {estatisticas?.total || listProf.length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaChalkboardTeacher className="text-2xl text-green-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Ativos</h3>
              {/* Professores Ativos */}
              <p className="text-2xl font-bold text-gray-900">
                {estatisticas?.ativos ||
                  listProf.filter((prof) => prof.status === "Ativo").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaChalkboardTeacher className="text-2xl text-red-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Inativos</h3>
              {/* Professores Inativos */}
              <p className="text-2xl font-bold text-gray-900">
                {estatisticas?.inativos ||
                  listProf.filter((prof) => prof.status === "Inativo").length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center">
            <FaChalkboardTeacher className="text-2xl text-purple-500 mr-3" />
            <div>
              <h3 className="text-sm font-medium text-gray-500">
                Especialidades
              </h3>
              {/* Especialidades */}
              <p className="text-2xl font-bold text-gray-900">
                {estatisticas?.especialidades ||
                  new Set(listProf.map((prof) => prof.especialidade)).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/*SEARCHANDFILTERS*/}
      <SearchAndFilters
        searchValue={searchText}
        onSearchChange={setSearchText}
        filters={searchFilters}
        searchPlaceholder="Buscar por nome ou email..."
        searchLabel="Buscar Professor"
      />
      {/* DATATABLE */}
      <DataTable
        columns={columns}
        data={professoresFiltrados} // Use os dados filtrados
        loading={loading}
        onEdit={handleEditProf}
        title="Lista de Professores"
        emptyMessage="Nenhum professor encontrado. Cadastre o primeiro professor!"
        itemsPerPage={10}
      />

      {/* TOAST*/}

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />

      {/* MODAL DO PROFESSOR */}
      {isModalOpen && (
        <ProfessorModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProfessor(null);
            setModalMode("create");
          }}
          onSuccess={() => {
            fetchProfessores();
            setToastMessage(
              modalMode === "create"
                ? "Professor cadastrado com sucesso!"
                : "Professor atualizado com sucesso!"
            );
            setToastType("success");
            setShowToast(true);
          }}
          mode={modalMode}
          professorData={selectedProfessor}
        />
      )}
    </div>
  );
}
